import { useState, useEffect, useCallback } from 'react';

interface ExitIntentOptions {
  threshold?: number;
  eventThrottle?: number;
  maxDisplays?: number;
}

export const useExitIntent = ({
  threshold = 20,
  eventThrottle = 100,
  maxDisplays = 1
}: ExitIntentOptions = {}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [displayCount, setDisplayCount] = useState(0);

  // Get display count from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('exitIntentDisplayCount');
    if (stored) {
      setDisplayCount(parseInt(stored, 10));
    }
  }, []);

  // Throttle function
  const throttle = useCallback((func: Function, limit: number) => {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }, []);

  // Mouse leave handler
  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (
      e.clientY <= threshold && 
      displayCount < maxDisplays && 
      !shouldShow
    ) {
      setShouldShow(true);
      const newCount = displayCount + 1;
      setDisplayCount(newCount);
      localStorage.setItem('exitIntentDisplayCount', newCount.toString());
    }
  }, [threshold, displayCount, maxDisplays, shouldShow]);

  // Set up and clean up event listeners
  useEffect(() => {
    const throttledHandler = throttle(handleMouseLeave, eventThrottle);
    document.addEventListener('mouseleave', throttledHandler);
    
    return () => {
      document.removeEventListener('mouseleave', throttledHandler);
    };
  }, [handleMouseLeave, eventThrottle, throttle]);

  const reset = useCallback(() => {
    setShouldShow(false);
  }, []);

  return {
    shouldShow,
    reset,
    displayCount
  };
};